import { useState, useEffect, useRef } from 'react'
import { Button } from '@/components/ui/button'
import { CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react'
import { cn } from '@/lib/utils'

// ─── Shared helpers ────────────────────────────────────────────────────────────

export const MONTH_NAMES = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
]

/** Format a "YYYY-MM" string as a human-readable month label, e.g. "Jan 2026" */
export function formatMonth(ym: string): string {
  const [year, month] = ym.split('-').map(Number)
  return `${MONTH_NAMES[month - 1]} ${year}`
}

/** Return the ISO week string "YYYY-Www" for the Monday of the week containing `date` */
export function getISOWeek(date: Date): string {
  const target = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()))
  const dayNr = (target.getUTCDay() + 6) % 7
  target.setUTCDate(target.getUTCDate() - dayNr + 3)
  const firstThursday = new Date(Date.UTC(target.getUTCFullYear(), 0, 4))
  const firstThursdayDay = (firstThursday.getUTCDay() + 6) % 7
  firstThursday.setUTCDate(firstThursday.getUTCDate() - firstThursdayDay + 3)
  const weekNo = Math.round((target.getTime() - firstThursday.getTime()) / (7 * 86400000)) + 1
  return `${target.getUTCFullYear()}-W${String(weekNo).padStart(2, '0')}`
}

/** Get the Monday Date for a given ISO week string "YYYY-Www" */
export function isoWeekToMonday(isoWeek: string): Date {
  const [yearStr, weekStr] = isoWeek.split('-W')
  const year = Number(yearStr)
  const week = Number(weekStr)
  const jan4 = new Date(year, 0, 4)
  const dayOfWeek = (jan4.getDay() + 6) % 7
  const week1Monday = new Date(jan4.getTime() - dayOfWeek * 86400000)
  return new Date(week1Monday.getTime() + (week - 1) * 7 * 86400000)
}

/** Format "YYYY-Www" as a compact label, e.g. "W3 · Jan 12–18" */
export function formatWeek(isoWeek: string): string {
  const monday = isoWeekToMonday(isoWeek)
  const sunday = new Date(monday.getTime() + 6 * 86400000)
  const fmt = (d: Date) => `${MONTH_NAMES[d.getMonth()]} ${d.getDate()}`
  const [, weekStr] = isoWeek.split('-W')
  return `W${Number(weekStr)} · ${fmt(monday)}–${fmt(sunday)}`
}

/** Count the number of ISO weeks in a given year */
export function isoWeeksInYear(year: number): number {
  const dec31 = new Date(year, 11, 31)
  return getISOWeek(dec31).startsWith(`${year}-`)
    ? Number(getISOWeek(dec31).split('-W')[1])
    : Number(getISOWeek(new Date(year, 11, 24)).split('-W')[1])
}

// ─── Component ────────────────────────────────────────────────────────────────

export interface PeriodGridPickerProps {
  mode: 'month' | 'week'
  value: string // "YYYY-MM" or "YYYY-Www"
  onChange: (v: string) => void
  disabled?: boolean
}

export function PeriodGridPicker({ mode, value, onChange, disabled }: PeriodGridPickerProps) {
  const now = new Date()
  const currentYear = now.getFullYear()
  const currentMonth = now.getMonth() + 1 // 1-based
  const currentISOWeek = getISOWeek(now)

  const valueYear = Number(value.split('-')[0])
  const [pickerYear, setPickerYear] = useState(valueYear)
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setPickerYear(Number(value.split('-')[0]))
  }, [value])

  useEffect(() => {
    if (!open) return
    function handle(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handle)
    return () => document.removeEventListener('mousedown', handle)
  }, [open])

  const label = mode === 'month' ? formatMonth(value) : formatWeek(value)

  if (mode === 'month') {
    return (
      <div ref={containerRef} className="relative">
        <Button
          variant="outline"
          size="sm"
          className="h-8 px-3 text-xs font-normal min-w-[110px] justify-between gap-1"
          onClick={() => setOpen((o) => !o)}
          disabled={disabled}
        >
          <span>{label}</span>
          <CalendarDays className="h-3 w-3 opacity-50 shrink-0" />
        </Button>
        {open && (
          <div className="absolute right-0 top-full mt-1 z-50 rounded-md border bg-popover text-popover-foreground shadow-md p-2 w-52">
            <div className="flex items-center justify-between mb-2 px-1">
              <button
                className="h-6 w-6 flex items-center justify-center rounded hover:bg-accent"
                onClick={() => setPickerYear((y) => y - 1)}
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <span className="text-sm font-medium">{pickerYear}</span>
              <button
                className={cn(
                  'h-6 w-6 flex items-center justify-center rounded hover:bg-accent',
                  pickerYear >= currentYear && 'opacity-30 pointer-events-none'
                )}
                onClick={() => setPickerYear((y) => y + 1)}
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
            <div className="grid grid-cols-3 gap-1">
              {MONTH_NAMES.map((name, idx) => {
                const monthNum = idx + 1
                const ym = `${pickerYear}-${String(monthNum).padStart(2, '0')}`
                const isFuture =
                  pickerYear > currentYear ||
                  (pickerYear === currentYear && monthNum >= currentMonth)
                const isSelected = ym === value
                return (
                  <button
                    key={ym}
                    disabled={isFuture}
                    onClick={() => { onChange(ym); setOpen(false) }}
                    className={cn(
                      'rounded px-1 py-1.5 text-xs transition-colors',
                      isSelected ? 'bg-primary text-primary-foreground font-medium' : 'hover:bg-accent',
                      isFuture && 'opacity-30 pointer-events-none'
                    )}
                  >
                    {name}
                  </button>
                )
              })}
            </div>
          </div>
        )}
      </div>
    )
  }

  // Week mode
  const totalWeeks = isoWeeksInYear(pickerYear)
  const weekNumbers = Array.from({ length: totalWeeks }, (_, i) => i + 1)

  return (
    <div ref={containerRef} className="relative">
      <Button
        variant="outline"
        size="sm"
        className="h-8 px-3 text-xs font-normal min-w-[168px] justify-between gap-1"
        onClick={() => setOpen((o) => !o)}
        disabled={disabled}
      >
        <span>{label}</span>
        <CalendarDays className="h-3 w-3 opacity-50 shrink-0" />
      </Button>
      {open && (
        <div className="absolute right-0 top-full mt-1 z-50 rounded-md border bg-popover text-popover-foreground shadow-md p-2 w-64">
          <div className="flex items-center justify-between mb-2 px-1">
            <button
              className="h-6 w-6 flex items-center justify-center rounded hover:bg-accent"
              onClick={() => setPickerYear((y) => y - 1)}
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="text-sm font-medium">{pickerYear}</span>
            <button
              className={cn(
                'h-6 w-6 flex items-center justify-center rounded hover:bg-accent',
                pickerYear >= currentYear && 'opacity-30 pointer-events-none'
              )}
              onClick={() => setPickerYear((y) => y + 1)}
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
          <div className="grid grid-cols-7 gap-0.5">
            {weekNumbers.map((wn) => {
              const wk = `${pickerYear}-W${String(wn).padStart(2, '0')}`
              const isFuture = wk >= currentISOWeek
              const isSelected = wk === value
              return (
                <button
                  key={wk}
                  title={formatWeek(wk)}
                  disabled={isFuture}
                  onClick={() => { onChange(wk); setOpen(false) }}
                  className={cn(
                    'rounded px-0.5 py-1 text-xs transition-colors leading-none',
                    isSelected ? 'bg-primary text-primary-foreground font-medium' : 'hover:bg-accent',
                    isFuture && 'opacity-30 pointer-events-none'
                  )}
                >
                  {wn}
                </button>
              )
            })}
          </div>
          <p className="mt-2 text-[10px] text-muted-foreground text-center leading-tight">
            {formatWeek(value)}
          </p>
        </div>
      )}
    </div>
  )
}
